import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import StreakCelebrationModal from "../components/StreakCelebrationModal";
import { 
  LogOut, 
  Settings, 
  RefreshCw, 
  Play, 
  Pause, 
  Volume2,
  VolumeX,
  Clock,
  Sparkles,
  ChevronRight,
  User,
  Shield,
  Sun,
  Award
} from "lucide-react";

const chakraData = [
  { name: "Root", sanskrit: "Muladhara", color: "#FF3B30", position: "base of spine" },
  { name: "Sacral", sanskrit: "Svadhisthana", color: "#FF9500", position: "lower abdomen" },
  { name: "Solar Plexus", sanskrit: "Manipura", color: "#FFCC00", position: "stomach area" },
  { name: "Heart", sanskrit: "Anahata", color: "#34C759", position: "center of chest" },
  { name: "Throat", sanskrit: "Vishuddha", color: "#30B0C7", position: "throat" },
  { name: "Third Eye", sanskrit: "Ajna", color: "#5856D6", position: "forehead" },
  { name: "Crown", sanskrit: "Sahasrara", color: "#AF52DE", position: "top of head" },
];

function ChakraOrb({ chakra, status, score, onClick, isSelected }) {
  const chakraInfo = chakraData.find(c => c.name.toLowerCase() === chakra.name.toLowerCase()) || chakraData[0];
  
  return (
    <motion.div
      onClick={onClick}
      className={`relative cursor-pointer group ${isSelected ? 'scale-110' : ''}`}
      whileHover={{ scale: 1.1 }}
      transition={{ duration: 0.2 }}
    >
      <div 
        className={`w-16 h-16 rounded-full chakra-orb ${status}`}
        style={{ 
          backgroundColor: chakraInfo.color,
          '--chakra-color': chakraInfo.color,
          boxShadow: `0 0 ${status === 'overactive' ? '30px' : '20px'} ${chakraInfo.color}${status === 'underactive' ? '40' : '80'}`
        }}
      />
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap">
        <span className="text-xs text-cosmic-text-muted">{chakra.name}</span>
      </div>
      {isSelected && (
        <motion.div 
          layoutId="chakra-ring"
          className="absolute inset-0 rounded-full border-2 border-white/50"
          initial={false}
        />
      )}
    </motion.div>
  );
}

function AudioPlayer({ audioUrl, title }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener('timeupdate', () => {
        const prog = (audioRef.current.currentTime / audioRef.current.duration) * 100;
        setProgress(prog || 0);
      });
      audioRef.current.addEventListener('loadedmetadata', () => {
        setDuration(audioRef.current.duration);
      });
      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false);
        setProgress(0);
      });
    }
  }, [audioUrl]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="glass-card p-6" data-testid="audio-player">
      <audio ref={audioRef} src={audioUrl} />
      
      <div className="flex items-center gap-4">
        <button
          onClick={togglePlay}
          className="w-14 h-14 rounded-full bg-cosmic-accent-gold flex items-center justify-center hover:scale-105 transition-transform"
          data-testid="play-pause-btn"
        >
          {isPlaying ? (
            <Pause className="w-6 h-6 text-cosmic-bg-primary" />
          ) : (
            <Play className="w-6 h-6 text-cosmic-bg-primary ml-1" />
          )}
        </button>
        
        <div className="flex-1">
          <p className="text-cosmic-text-primary font-medium mb-2">{title}</p>
          <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="absolute left-0 top-0 h-full bg-cosmic-accent-gold transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-cosmic-text-muted">
              {formatTime(audioRef.current?.currentTime || 0)}
            </span>
            <span className="text-xs text-cosmic-text-muted">
              {formatTime(duration)}
            </span>
          </div>
        </div>
        
        <button
          onClick={toggleMute}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5 text-cosmic-text-muted" />
          ) : (
            <Volume2 className="w-5 h-5 text-cosmic-text-secondary" />
          )}
        </button>
      </div>
    </div>
  );
}

// Generate emotional context message based on chakra analysis
const getEmotionalMessage = (chakraAnalysis, userName) => {
  if (!chakraAnalysis || chakraAnalysis.length === 0) {
    return "Your inner journey begins with awareness...";
  }
  
  const underactiveChakras = chakraAnalysis.filter(c => c.status === 'underactive');
  const overactiveChakras = chakraAnalysis.filter(c => c.status === 'overactive');
  
  if (underactiveChakras.length > 0) {
    const chakra = underactiveChakras[0];
    const messages = {
      'root': "Your mind is seeking stillness today. Ground yourself in the present moment.",
      'sacral': "Your creative energy wants expression. Allow yourself to feel and flow.",
      'solar plexus': "Your inner fire is waiting to be kindled. Trust your personal power.",
      'heart': "Your heart is opening to deeper connection. Practice compassion, starting with yourself.",
      'throat': "Your voice wants to be heard. Speak your truth with gentle confidence.",
      'third eye': "Your intuition is stirring. Trust the wisdom that lives within you.",
      'crown': "Your spirit seeks expansion. Connect with the infinite within."
    };
    return messages[chakra.name.toLowerCase()] || "Your energy is finding its balance. Be patient with yourself.";
  }
  
  if (overactiveChakras.length > 0) {
    return "Your energy is intense today. Channel it through mindful practice.";
  }
  
  return "You are aligned with your inner light today. Keep walking your sacred path.";
};

// Calculate days since assessment (simulating 21-day practice journey)
const calculateDayProgress = (assessmentDate) => {
  if (!assessmentDate) return { day: 1, message: "Day 1 of your transformation" };
  
  const created = new Date(assessmentDate);
  const now = new Date();
  const diffDays = Math.floor((now - created) / (1000 * 60 * 60 * 24)) + 1;
  const dayInJourney = Math.min(diffDays, 21);
  
  if (dayInJourney <= 7) {
    return { day: dayInJourney, message: `Day ${dayInJourney} — Building your foundation` };
  } else if (dayInJourney <= 14) {
    return { day: dayInJourney, message: `Day ${dayInJourney} — Deepening your practice` };
  } else if (dayInJourney < 21) {
    return { day: dayInJourney, message: `Day ${dayInJourney} — Transformation in progress` };
  } else {
    return { day: 21, message: "Congratulations! You've completed your 21-day journey" };
  }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChakra, setSelectedChakra] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [dayProgress, setDayProgress] = useState({ day: 1, message: "" });
  const [emotionalMessage, setEmotionalMessage] = useState("");
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);

  useEffect(() => {
    fetchAssessment();
    fetchAudioFiles();
    checkForCelebration();
  }, []);

  const checkForCelebration = async () => {
    try {
      const response = await axios.get(`${API}/maya-manthan/streak/celebration`);
      if (response.data.has_celebration) {
        setCelebrationData(response.data.celebration);
        setTimeout(() => setShowCelebration(true), 1500);
      }
    } catch (error) {
      // Maya Manthan endpoint might not be available for all users
      console.log("Celebration check:", error.message);
    }
  };

  const fetchAssessment = async () => {
    try {
      const response = await axios.get(`${API}/assessment`);
      setAssessment(response.data);
      if (response.data.chakra_analysis?.length > 0) {
        setSelectedChakra(response.data.chakra_analysis[0]);
        setEmotionalMessage(getEmotionalMessage(response.data.chakra_analysis, user?.name));
      }
      if (response.data.created_at) {
        setDayProgress(calculateDayProgress(response.data.created_at));
      }
    } catch (error) {
      if (error.response?.status === 404) {
        navigate('/questionnaire');
      } else {
        console.error("Error fetching assessment:", error);
        toast.error("Failed to load your assessment");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAudioFiles = async () => {
    try {
      const response = await axios.get(`${API}/audio`);
      setAudioFiles(response.data);
    } catch (error) {
      console.error("Error fetching audio:", error);
    }
  };

  const trackEvent = async (eventType) => {
    try {
      await axios.post(`${API}/track-event?event_type=${eventType}`);
    } catch (error) {
      console.error("Track event error:", error);
    }
  };

  const handleUpgradeClick = () => {
    trackEvent('clicked_upgrade');
    toast.success("Thank you for your interest! We'll be in touch soon.");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-bg-primary flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cosmic-accent-gold/30 border-t-cosmic-accent-gold rounded-full animate-spin"></div>
      </div>
    );
  }

  const chakraAnalysis = assessment?.chakra_analysis || [];
  const meditationPlan = assessment?.meditation_plan || [];

  return (
    <div className="min-h-screen bg-cosmic-bg-primary" data-testid="user-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-20 lg:w-64 bg-cosmic-bg-secondary/80 backdrop-blur-xl border-r border-white/5 z-40">
        <div className="flex flex-col h-full p-4">
          {/* Logo */}
          <div className="mb-8 lg:px-4">
            <span className="font-heading text-xl gold-text hidden lg:block">ChakraFlow</span>
            <span className="font-heading text-xl gold-text lg:hidden text-center block">CF</span>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 space-y-2">
            <button className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 text-cosmic-text-primary">
              <Sparkles className="w-5 h-5" />
              <span className="hidden lg:block">Dashboard</span>
            </button>
            <button 
              onClick={() => navigate('/check-in')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 text-cosmic-text-secondary transition-colors"
              data-testid="checkin-nav-btn"
            >
              <RefreshCw className="w-5 h-5" />
              <span className="hidden lg:block">Check-in</span>
            </button>
            <button 
              onClick={() => navigate('/maya-manthan')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 text-cosmic-text-secondary transition-colors"
              data-testid="maya-manthan-nav-btn"
            >
              <Sun className="w-5 h-5" />
              <span className="hidden lg:block">Maya Manthan</span>
            </button>
            {user?.role === 'admin' && (
              <button 
                onClick={() => navigate('/admin')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 text-cosmic-text-secondary transition-colors"
                data-testid="admin-nav-btn"
              >
                <Shield className="w-5 h-5" />
                <span className="hidden lg:block">Admin</span>
              </button>
            )}
          </nav>

          {/* User Section */}
          <div className="border-t border-white/5 pt-4 space-y-2">
            <div className="flex items-center gap-3 px-4 py-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-cosmic-accent-violet flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
              <div className="hidden lg:block flex-1 min-w-0">
                <p className="text-sm text-cosmic-text-primary truncate">{user?.name}</p>
                <p className="text-xs text-cosmic-text-muted truncate">{user?.email}</p>
              </div>
            </div>
            <button 
              onClick={logout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 text-cosmic-text-muted transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden lg:block">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-20 lg:ml-64 min-h-screen p-6 lg:p-8">
        {/* Header with Emotional Context */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          {/* Day Progress Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cosmic-accent-gold/10 border border-cosmic-accent-gold/20 mb-4"
          >
            <div className="w-2 h-2 rounded-full bg-cosmic-accent-gold animate-pulse" />
            <span className="text-cosmic-accent-gold text-sm font-medium">{dayProgress.message}</span>
          </motion.div>
          
          <h1 className="font-heading text-3xl lg:text-4xl text-cosmic-text-primary mb-3">
            Your Energy Altar
          </h1>
          
          {/* Emotional Context Message */}
          <div className="bg-gradient-to-r from-cosmic-accent-violet/10 to-transparent border-l-2 border-cosmic-accent-violet p-4 rounded-r-xl mt-4">
            <p className="text-cosmic-text-secondary italic font-light text-lg">
              {emotionalMessage || `Welcome back, ${user?.name?.split(' ')[0]}. Here's your chakra status.`}
            </p>
          </div>
        </motion.div>

        {/* Chakra Visualization */}
        <motion.section 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-8 mb-8"
          data-testid="chakra-visualization"
        >
          <h2 className="font-heading text-2xl text-cosmic-text-primary mb-8 text-center">
            Your Seven Chakras
          </h2>
          
          <div className="flex justify-center items-end gap-6 lg:gap-10 flex-wrap mb-12">
            {chakraAnalysis.map((chakra, index) => (
              <ChakraOrb
                key={chakra.name}
                chakra={chakra}
                status={chakra.status}
                score={chakra.score}
                onClick={() => setSelectedChakra(chakra)}
                isSelected={selectedChakra?.name === chakra.name}
              />
            ))}
          </div>

          {/* Selected Chakra Details */}
          <AnimatePresence mode="wait">
            {selectedChakra && (
              <motion.div
                key={selectedChakra.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white/5 rounded-2xl p-6 max-w-2xl mx-auto"
                data-testid="selected-chakra-details"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ 
                      backgroundColor: chakraData.find(c => c.name.toLowerCase() === selectedChakra.name.toLowerCase())?.color 
                    }}
                  />
                  <div>
                    <h3 className="font-heading text-xl text-cosmic-text-primary">
                      {selectedChakra.name} Chakra
                    </h3>
                    <p className="text-sm text-cosmic-text-muted">{selectedChakra.sanskrit_name}</p>
                  </div>
                  <div className="ml-auto">
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      selectedChakra.status === 'balanced' ? 'bg-status-success/20 text-status-success' :
                      selectedChakra.status === 'underactive' ? 'bg-status-warning/20 text-status-warning' :
                      'bg-status-error/20 text-status-error'
                    }`}>
                      {selectedChakra.status}
                    </span>
                  </div>
                </div>
                
                <p className="text-cosmic-text-secondary mb-4">{selectedChakra.description}</p>
                
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-cosmic-text-muted">Balance Score</span>
                    <span className="text-cosmic-text-primary">{selectedChakra.score}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full transition-all duration-500"
                      style={{ 
                        width: `${selectedChakra.score}%`,
                        backgroundColor: chakraData.find(c => c.name.toLowerCase() === selectedChakra.name.toLowerCase())?.color
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-cosmic-text-primary mb-2">Recommendations</h4>
                  <ul className="space-y-1">
                    {selectedChakra.recommendations?.map((rec, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-cosmic-text-secondary">
                        <ChevronRight className="w-4 h-4 text-cosmic-accent-gold" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>

        {/* AI Insights */}
        {assessment?.ai_insights && (
          <motion.section 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-6 mb-8"
            data-testid="ai-insights"
          >
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-5 h-5 text-cosmic-accent-gold" />
              <h2 className="font-heading text-xl text-cosmic-text-primary">AI Insights</h2>
            </div>
            <p className="text-cosmic-text-secondary leading-relaxed">{assessment.ai_insights}</p>
          </motion.section>
        )}

        {/* Meditation Plan */}
        <motion.section 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
          data-testid="meditation-plan-section"
        >
          <h2 className="font-heading text-2xl text-cosmic-text-primary mb-6">
            Your Meditation Plan
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {meditationPlan.map((meditation, index) => {
              const relatedAudio = audioFiles.find(a => 
                meditation.chakra_focus?.some(c => c.toLowerCase() === a.chakra.toLowerCase())
              );
              
              return (
                <motion.div
                  key={index}
                  className="glass-card p-6"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                  data-testid={`meditation-card-${index}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-heading text-xl text-cosmic-text-primary mb-1">
                        {meditation.title}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-cosmic-text-muted">
                        <Clock className="w-4 h-4" />
                        {meditation.duration_minutes} minutes
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {meditation.chakra_focus?.map(chakra => {
                        const color = chakraData.find(c => c.name.toLowerCase() === chakra.toLowerCase())?.color;
                        return (
                          <div 
                            key={chakra}
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: color }}
                            title={chakra}
                          />
                        );
                      })}
                    </div>
                  </div>
                  
                  <p className="text-cosmic-text-secondary text-sm mb-4">
                    {meditation.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {meditation.techniques?.map((tech, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1 rounded-full bg-white/5 text-xs text-cosmic-text-muted"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {relatedAudio && (
                    <AudioPlayer 
                      audioUrl={`${API}/uploads/audio/${relatedAudio.filename}`}
                      title={relatedAudio.title}
                    />
                  )}
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* Upgrade CTA */}
        <motion.section 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="glass-card p-8 text-center border-cosmic-accent-gold/20"
          data-testid="upgrade-cta"
        >
          <h2 className="font-heading text-2xl text-cosmic-text-primary mb-4">
            Ready for Deeper Transformation?
          </h2>
          <p className="text-cosmic-text-secondary mb-6 max-w-2xl mx-auto">
            Unlock our premium meditation programs with personalized 1-on-1 guidance, 
            advanced energy healing techniques, and exclusive guided sessions.
          </p>
          <Button 
            onClick={handleUpgradeClick}
            className="btn-gold"
            data-testid="upgrade-btn"
          >
            Explore Premium Programs
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.section>
      </main>
      
      {/* Streak Celebration Modal */}
      <StreakCelebrationModal
        isOpen={showCelebration}
        onClose={() => setShowCelebration(false)}
        celebration={celebrationData}
        onCelebrated={() => setCelebrationData(null)}
      />
    </div>
  );
}
